import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import { cn } from '../../../utils/cn';
import Icon from '../../../components/AppIcon';

const CalendarSidebar = ({ 
  userCalendars, 
  selectedUser, 
  onUserSelect, 
  onNewEvent,
  onRequestAppointment,
  calendarEvents,
  isCollapsed,
  onToggleCollapse,
  isNotStudent
}) => {
  const navigate = useNavigate();
  
  const getEventTypeStats = () => {
    console.log('🔍 CalendarSidebar: calculando estatísticas para eventos:', calendarEvents);
    
    const stats = {
      atendimento: 0,
      disponivel: 0,
      bloqueado: 0,
      evento: 0
    };
    
    calendarEvents?.forEach(event => {
      console.log('🔍 CalendarSidebar: processando evento:', {
        id: event.id,
        type: event.type,
        title: event.title
      });
      stats[event.type] = (stats?.[event?.type] || 0) + 1;
    });
    
    console.log('🔍 CalendarSidebar: estatísticas calculadas:', stats);
    return stats;
  };

  const stats = getEventTypeStats();
  const currentCalendar = userCalendars?.find(cal => cal?.id === selectedUser);

  return (
    <div className={`${isCollapsed ? 'w-16' : 'w-80'} bg-card border-r border-border p-6 overflow-y-auto transition-all duration-300 relative`}>
      {/* Toggle Button */}
      <div className="absolute top-2 right-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleCollapse}
          className="h-8 w-8"
        >
          <Icon name={isCollapsed ? "ChevronRight" : "ChevronLeft"} size={16} />
        </Button>
      </div>

      <div className={`space-y-6 ${isCollapsed ? 'hidden' : ''}`}>
        {/* Quick Actions */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-foreground">Ações Rápidas</h3>
          
          {currentCalendar?.isOwn ? (
            <div className="space-y-2">
              <Button
                variant="default"
                fullWidth
                iconName="Plus"
                iconPosition="left"
                onClick={onNewEvent}
              >
                Novo Agendamento
              </Button>
              <Button
                variant="outline"
                fullWidth
                iconName="Settings"
                iconPosition="left"
                onClick={() => navigate('/availability-configuration')}
              >
                Configurar Disponibilidade
              </Button>
              {isNotStudent && (
                <Button
                  variant="ghost"
                  fullWidth
                  iconName="Inbox"
                  iconPosition="left"
                  onClick={() => navigate('/request-management-interface')}
                >
                  Solicitações Recebidas
                  <span className="ml-auto bg-warning text-warning-foreground text-xs px-2 py-0.5 rounded-full">
                    3
                  </span>
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <Button
                variant="secondary"
                fullWidth
                iconName="MessageSquare"
                iconPosition="left"
                onClick={onRequestAppointment}
              >
                Solicitar Atendimento
              </Button>
              <Button
                variant="outline"
                fullWidth
                iconName="Eye"
                iconPosition="left"
              >
                Visualizar Disponibilidade
              </Button>
            </div>
          )}
        </div>

        {/* Calendar Selection */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-foreground">Calendários</h3>
          
          <div className="space-y-2">
            {userCalendars?.map(calendar => (
              <div
                key={calendar?.id}
                className={cn(
                  "flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors",
                  selectedUser === calendar?.id
                    ? "bg-primary/10 border border-primary/20" :"hover:bg-muted/50 border border-transparent"
                )}
                onClick={() => onUserSelect(calendar?.id)}
              >
                <div 
                  className="w-4 h-4 rounded-full border-2"
                  style={{ backgroundColor: calendar?.color }}
                />
                <div className="flex-1">
                  <p className="font-medium text-foreground">{calendar?.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {calendar?.isOwn ? 'Seu calendário' : 'Apenas visualização'}
                  </p>
                </div>
                {selectedUser === calendar?.id && (
                  <div className="w-2 h-2 bg-primary rounded-full" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Calendar Stats */}
        {currentCalendar?.isOwn && (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-foreground">Resumo</h3>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-background/50 border border-border rounded-lg p-3">
                <div className="flex items-center space-x-2 mb-1">
                  <span className="text-primary">📅</span>
                  <span className="text-xs text-muted-foreground">Atendimentos</span>
                </div>
                <p className="text-lg font-bold text-primary">{stats?.atendimento}</p>
              </div>
              
              <div className="bg-background/50 border border-border rounded-lg p-3">
                <div className="flex items-center space-x-2 mb-1">
                  <span className="text-success">🔓</span>
                  <span className="text-xs text-muted-foreground">Disponível</span>
                </div>
                <p className="text-lg font-bold text-success">{stats?.disponivel}</p>
              </div>
              
              <div className="bg-background/50 border border-border rounded-lg p-3">
                <div className="flex items-center space-x-2 mb-1">
                  <span className="text-error">🚫</span>
                  <span className="text-xs text-muted-foreground">Ocupado</span>
                </div>
                <p className="text-lg font-bold text-primary">{stats?.bloqueado}</p>
              </div>
              
              <div className="bg-background/50 border border-border rounded-lg p-3">
                <div className="flex items-center space-x-2 mb-1">
                  <span className="text-secondary">🎯</span>
                  <span className="text-xs text-muted-foreground">Eventos</span>
                </div>
                <p className="text-lg font-bold text-primary">{stats?.evento}</p>
              </div>
            </div>
          </div>
        )}

        {/* Event Types Legend */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-foreground">Legenda</h3>
          
          <div className="space-y-2 text-sm">
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-primary/20 border border-primary/30 rounded"></div>
              <span className="text-foreground">Atendimento</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-success/20 border border-success/30 rounded"></div>
              <span className="text-foreground">Disponível</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-error/20 border border-error/30 rounded"></div>
              <span className="text-foreground">Ocupado</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-secondary/20 border border-secondary/30 rounded"></div>
              <span className="text-foreground">Evento</span>
            </div>
          </div>
        </div>

        {/* Mini Calendar */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-foreground">Navegação Rápida</h3>
          
          <div className="bg-background/50 border border-border rounded-lg p-3">
            <p className="text-sm text-muted-foreground text-center">
              Mini calendário<br />
              <span className="text-xs">(Integração futura)</span>
            </p>
            
            {/* Placeholder for mini calendar */}
            <div className="grid grid-cols-7 gap-1 mt-3 text-xs">
              {['D', 'S', 'T', 'Q', 'Q', 'S', 'S']?.map((day, index) => (
                <div key={`day-${index}`} className="text-center text-muted-foreground p-1">
                  {day}
                </div>
              ))}
              {Array.from({ length: 31 }, (_, i) => i + 1)?.map(date => (
                <div 
                  key={date} 
                  className="text-center p-1 hover:bg-primary/10 rounded cursor-pointer"
                >
                  {date}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarSidebar;